import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { searchPreprints, PreprintRow } from "../lib/api";
import ErrorBox from "../components/ErrorBox";

function Chip({
  className,
  children,
  title,
}: {
  className: string;
  children: ReactNode;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium ${className}`}
    >
      {children}
    </span>
  );
}

/**
 * Reproducibility-scan cell for the paper table:
 *   - generated scan complete → "X/Y" checks passed (out of the full instrument)
 *   - a manual audit exists    → "Manual" chip
 *   - queued for the scanner   → "Scan Queued"
 *   - scanner gave up          → "Scan Failed"
 *   - never queued / no scan    → "—"
 */
function ReproCell({ p }: { p: PreprintRow }) {
  if (
    p.summary_source === "generated" &&
    p.repro_passed != null &&
    p.repro_total != null
  ) {
    return (
      <span
        title="Checks passed out of the full instrument (auto-generated scan)"
        className="font-medium text-ink-900"
      >
        {p.repro_passed}/{p.repro_total}
      </span>
    );
  }
  if (p.summary_source === "manual")
    return <Chip className="bg-sky-100 text-sky-700">Manual</Chip>;
  if (p.repro_status === "pending")
    return <Chip className="bg-amber-100 text-amber-700">Scan Queued</Chip>;
  if (p.repro_status === "failed")
    return (
      <Chip
        className="bg-red-100 text-red-700"
        title={p.repro_error ?? undefined}
      >
        Scan Failed
      </Chip>
    );
  return <span className="text-ink-400">—</span>;
}

/**
 * Paper-selection screen.
 *
 * Search bar parses:
 *   - DOI / egusphere_id   → exact match (frontend simply forwards to the API,
 *                            which already handles all three forms)
 *   - `since:YYYY-MM-DD`   → date filter (extracted client-side so the API
 *                            sees a clean `since` param)
 *   - free text            → ILIKE title / lead_author
 *   - empty                → most-recent first
 *
 * We debounce the search at 300 ms so each keystroke doesn't hit the API.
 */

const SINCE_TOKEN_RE = /\bsince:(\d{4}-\d{2}-\d{2})\b/i;
const DEBOUNCE_MS = 300;

export default function PaperSelection() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PreprintRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const { freeText, since } = useMemo(() => {
    const m = query.match(SINCE_TOKEN_RE);
    if (!m) return { freeText: query.trim(), since: undefined as string | undefined };
    return {
      freeText: (query.slice(0, m.index) + query.slice(m.index! + m[0].length))
        .trim(),
      since: m[1],
    };
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await searchPreprints(freeText, since);
        if (!cancelled) setResults(rows);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setResults(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [freeText, since]);

  return (
    <div className="mx-auto flex h-full max-w-5xl flex-col gap-4 px-6 py-6">
      <h1 className="text-xl font-semibold">Find a preprint to review</h1>
      <div className="flex flex-col gap-2">
        <input
          autoFocus
          aria-label="Search preprints"
          placeholder="Search by DOI, title, lead author, or since:YYYY-MM-DD — empty for all"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded border border-ink-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-ink-500 focus:outline-none"
        />
        <div className="text-xs text-ink-500">
          {loading
            ? "Searching…"
            : results
              ? `${results.length} preprint${results.length === 1 ? "" : "s"} found`
              : error
                ? ""
                : "Loading recent preprints…"}
          {since ? ` · since ${since}` : ""}
        </div>
      </div>

      {error && <ErrorBox title="Search failed" detail={error} />}

      <div className="flex-1 overflow-auto rounded border border-ink-200 bg-white shadow-sm">
        <table className="w-full table-fixed text-sm">
          <thead className="sticky top-0 z-10 border-b border-ink-200 bg-ink-100 text-left">
            <tr>
              <th className="w-24 px-3 py-2 font-medium">Posted</th>
              <th className="w-40 px-3 py-2 font-medium">Lead author</th>
              <th className="px-3 py-2 font-medium">Title</th>
              <th
                className="w-20 px-3 py-2 font-medium"
                title="Number of code/data archives (e.g. Zenodo) found for this preprint"
              >
                <span className="cursor-help underline decoration-dotted underline-offset-2">
                  Deposits
                </span>
              </th>
              <th
                className="w-28 px-3 py-2 font-medium"
                title="Reproducibility scan: checks passed out of the full instrument once complete, or the scan's queue state"
              >
                <span className="cursor-help underline decoration-dotted underline-offset-2">
                  Repro
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {results?.map((p) => (
              <tr
                key={p.egusphere_id}
                onClick={() => navigate(`/p/${p.egusphere_id}`)}
                className="cursor-pointer border-b border-ink-100 hover:bg-ink-50"
              >
                <td className="px-3 py-2 text-ink-500">{p.posting_date}</td>
                <td className="px-3 py-2 text-ink-900">
                  {p.lead_author}
                  {p.coauthors.length > 0 && (
                    <span className="ml-1 text-ink-500">
                      +{p.coauthors.length}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-ink-900">{p.title}</td>
                <td className="px-3 py-2 text-ink-500">
                  {p.deposit_count ?? "—"}
                </td>
                <td className="px-3 py-2">
                  <ReproCell p={p} />
                </td>
              </tr>
            ))}
            {results && results.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-ink-500">
                  No preprints match this query.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
